import { demoActor, demoIdentities } from "../auth/demo-actors";
import type { AdminRepository } from "./admin-repository";
import {
  assertRequestAuthority,
  authorityFingerprint,
  captureRequestAuthority,
  observedCatalogueActor,
} from "./authority";
import type { DemoMediaCommand, DemoMediaResults } from "./demo-media-contract";
import {
  type DemoWorkspaceMethod,
  demoWorkspaceProtocol,
  demoWorkspaceWrites,
  isDemoWorkspaceMethod,
} from "./demo-workspace-contract";
import { type ApiErrorBody, CyraError } from "./errors";
import {
  MutationIntents,
  mutationFingerprint,
  mutationOperationFingerprint,
} from "./mutation-intents";
import {
  createOutcomeDefinitions,
  demoCreateBinding,
  demoUpdateBinding,
  demoUpdateOperationArgs,
  existingMutationDefinitions,
  type OutcomeOperation,
} from "./mutation-outcome-registry";
import { type CurrentLegalDocuments, parsePublicLegalDocuments } from "./public-legal-projection";

type Fetcher = typeof fetch;
type Capability = {
  available: boolean;
  requiresReload: boolean;
  persistence: "LOCAL_HELPER_MEMORY" | "IN_MEMORY";
  idleMs?: number;
  absoluteMs?: number;
};
export class DemoWorkspaceConnection {
  private discovery: Promise<Capability> | undefined;
  private session: Promise<void> | undefined;
  private mode: "unknown" | "local" | "memory" = "unknown";
  constructor(private fetcher: Fetcher = (...args) => fetch(...args)) {}
  async capability(): Promise<Capability> {
    this.discovery ??= this.discover();
    return this.discovery;
  }
  private async discover(): Promise<Capability> {
    try {
      const response = await this.fetcher("/__cyra_demo_csv/health", {
        cache: "no-store",
        credentials: "same-origin",
        signal: AbortSignal.timeout(2000),
      });
      const body = (await response.json()) as {
        available?: boolean;
        synthetic?: boolean;
        protocol?: string;
        idleMs?: number;
        absoluteMs?: number;
      };
      if (
        response.ok &&
        body.available &&
        body.synthetic &&
        body.protocol === demoWorkspaceProtocol
      ) {
        this.mode = "local";
        return {
          available: true,
          requiresReload: false,
          persistence: "LOCAL_HELPER_MEMORY",
          ...(body.idleMs ? { idleMs: body.idleMs } : {}),
          ...(body.absoluteMs ? { absoluteMs: body.absoluteMs } : {}),
        };
      }
    } catch {}
    this.mode = "memory";
    return { available: false, requiresReload: true, persistence: "IN_MEMORY" };
  }
  async checkCapability(): Promise<Capability> {
    // Never merge an already-used in-memory catalogue into a different server workspace.
    if (this.mode === "memory")
      return { available: false, requiresReload: true, persistence: "IN_MEMORY" };
    return this.capability();
  }
  private async post(path: string, body: unknown, signal?: AbortSignal): Promise<Response> {
    try {
      return await this.fetcher(`/__cyra_demo_csv/${path}`, {
        method: "POST",
        credentials: "same-origin",
        cache: "no-store",
        headers: {
          "content-type": "application/json",
          "x-cyra-demo-helper": "synthetic-local-only",
        },
        body: JSON.stringify(body),
        signal: signal
          ? AbortSignal.any([signal, AbortSignal.timeout(30000)])
          : AbortSignal.timeout(30000),
      });
    } catch (error) {
      if (signal?.aborted) throw error;
      throw new CyraError({
        code: "DEMO_HELPER_UNAVAILABLE",
        requestId: "local-synthetic-workspace",
        message:
          "The local synthetic helper is unavailable. A submitted change may already have been recorded. Reconnect and refresh its status before starting another intent.",
      });
    }
  }
  private async result(response: Response): Promise<unknown> {
    const body = (await response.json()) as ApiErrorBody & { result?: unknown };
    if (!response.ok) {
      if (body.error && typeof body.error === "object")
        throw new CyraError({ ...body.error, status: response.status });
      throw new CyraError({
        code: "DEMO_HELPER_UNAVAILABLE",
        requestId: "local-synthetic-workspace",
        message: "The local helper response could not be reconciled.",
        status: response.status,
      });
    }
    return body.result;
  }
  private async ensureSession() {
    this.session ??= this.post("session", {})
      .then((response) => this.result(response))
      .then(() => undefined)
      .catch((error) => {
        this.session = undefined;
        throw error;
      });
    await this.session;
  }
  async reset(): Promise<void> {
    const authority = captureRequestAuthority();
    if (!(await this.capability()).available)
      throw new CyraError({
        code: "DEMO_HELPER_UNAVAILABLE",
        requestId: "local-synthetic-workspace",
        message: "Start the helper, then reload this page before resetting a synthetic workspace.",
      });
    assertRequestAuthority(authority);
    await this.result(await this.post("new-workspace", { consequenceConfirmed: true }));
    assertRequestAuthority(authority);
    this.session = Promise.resolve();
  }
  async publicLegalDocuments(): Promise<CurrentLegalDocuments> {
    if (!(await this.capability()).available)
      throw new CyraError({
        code: "DEMO_HELPER_UNAVAILABLE",
        requestId: "local-synthetic-workspace",
        message: "The local workspace is unavailable. Reload to reconnect.",
      });
    await this.ensureSession();
    return parsePublicLegalDocuments(await this.result(await this.post("public-legal", {})));
  }
  async publicInvitation(
    operation: "activate" | "inspect",
    invitationId: string,
  ): Promise<unknown> {
    if (!(await this.capability()).available)
      throw new CyraError({
        code: "DEMO_HELPER_UNAVAILABLE",
        requestId: "local-synthetic-workspace",
        message: "The local workspace is unavailable. Reload to reconnect.",
      });
    await this.ensureSession();
    return this.result(await this.post("public-invitation", { invitationId, operation }));
  }
  async media<C extends DemoMediaCommand>(
    command: C,
    signal?: AbortSignal,
  ): Promise<DemoMediaResults[C["operation"]]> {
    const authority = captureRequestAuthority();
    const actor = observedCatalogueActor();
    const identity = demoIdentities.find((value) => actor?.userId === demoActor(value).userId);
    if (
      !actor ||
      !identity ||
      authorityFingerprint(actor) !== authorityFingerprint(demoActor(identity))
    )
      throw new CyraError({
        code: "AUTH_PERMISSION_DENIED",
        message: "Current synthetic identity is required for private profile media.",
        requestId: "local-synthetic-workspace",
      });
    if (!(await this.capability()).available)
      throw new CyraError({
        code: "DEMO_HELPER_UNAVAILABLE",
        message:
          "Profile images require the local demo helper. Start it and reload this workspace.",
        requestId: "local-synthetic-workspace",
      });
    assertRequestAuthority(authority);
    await this.ensureSession();
    assertRequestAuthority(authority);
    const result = await this.result(
      await this.post("media", { protocol: demoWorkspaceProtocol, identity, command }, signal),
    );
    assertRequestAuthority(authority);
    return result as DemoMediaResults[C["operation"]];
  }
  async invoke(method: DemoWorkspaceMethod, original: unknown[]): Promise<unknown> {
    const authority = captureRequestAuthority(),
      actor = observedCatalogueActor();
    const identity = demoIdentities.find((value) => actor?.userId === demoActor(value).userId);
    if (!actor || !identity)
      throw new CyraError({
        code: "AUTH_PERMISSION_DENIED",
        message: "Only the named synthetic fixtures can use the local workspace.",
        requestId: "local-synthetic-workspace",
      });
    if (authorityFingerprint(actor) !== authorityFingerprint(demoActor(identity)))
      throw new CyraError({
        code: "AUTH_PERMISSION_DENIED",
        message:
          "Synthetic actor authority changed. Reconcile the selected fixture before continuing.",
        requestId: "local-synthetic-workspace",
      });
    const args = [...original];
    const signal = args.at(-1) instanceof AbortSignal ? (args.pop() as AbortSignal) : undefined;
    while (args.length && args.at(-1) === undefined) args.pop();
    await this.ensureSession();
    assertRequestAuthority(authority);
    if (signal?.aborted) throw signal.reason ?? new DOMException("Request aborted.", "AbortError");
    const body = { protocol: demoWorkspaceProtocol, identity, method, args };
    const writable = Object.hasOwn(demoWorkspaceWrites, method);
    const intents = writable ? new MutationIntents(window.sessionStorage) : undefined;
    const fingerprint = intents
      ? await mutationFingerprint(
          actor.userId,
          new Request(new URL("/__cyra_demo_csv/command", window.location.origin), {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(body),
          }),
        )
      : undefined;
    const sourceFingerprint = intents
      ? await demoMutationOperationFingerprint(actor.userId, method, args)
      : undefined;
    const createBinding = demoCreateBinding(method, args);
    const updateBinding = demoUpdateBinding(method, args);
    const updateOwner = updateBinding
      ? existingMutationDefinitions[updateBinding.operation]
      : undefined;
    const updateDemoMethod =
      updateOwner && "demoMethod" in updateOwner ? updateOwner.demoMethod : undefined;
    const operationFingerprint = updateBinding
      ? updateDemoMethod
        ? await demoMutationOperationFingerprint(
            actor.userId,
            updateDemoMethod,
            demoUpdateOperationArgs(updateBinding.operation, updateBinding.targetId),
          )
        : undefined
      : createBinding
        ? await demoMutationOperationFingerprint(
            actor.userId,
            createOutcomeDefinitions[createBinding.operation].demoMethod,
            createBinding.targetId ? [createBinding.targetId] : [],
          )
        : sourceFingerprint;
    const conflictingScopes =
      updateBinding?.operation === "LEGAL_DRAFT_UPDATE"
        ? [await demoMutationOperationFingerprint(actor.userId, "saveLegalDraft", [])]
        : [];
    assertRequestAuthority(authority);
    const intent =
      intents && fingerprint && operationFingerprint
        ? intents.reserve(
            fingerprint,
            crypto.randomUUID(),
            operationFingerprint,
            sourceFingerprint !== operationFingerprint ? sourceFingerprint : undefined,
            conflictingScopes,
          )
        : undefined;
    // Parse and fence before completing a receipt: a lost/malformed response remains uncertain.
    try {
      const response = await this.post(
        "command",
        { ...body, intentId: intent?.key ?? null },
        signal,
      );
      assertRequestAuthority(authority);
      const result = await this.result(response);
      assertRequestAuthority(authority);
      if (createBinding || updateBinding) {
        const legal =
          method !== "createApprovalRequest" &&
          (createBinding?.operation ?? updateBinding?.operation ?? "").startsWith("LEGAL_");
        const { assertOwnedMutationResult } = await import("./owned-mutation-result");
        assertOwnedMutationResult(
          legal && result && typeof result === "object" && "revision" in result
            ? { ...result, version: result.revision }
            : result,
          method === "createApprovalRequest" ? undefined : (createBinding ?? updateBinding),
          "demo",
        );
        assertRequestAuthority(authority);
      }
      if (intent && intents) intents.complete(intent);
      return result;
    } catch (error) {
      if (
        intent &&
        intents &&
        error instanceof CyraError &&
        error.mutationDisposition === "NOT_APPLIED"
      )
        intents.notApplied(intent);
      else if (intent && intents) intents.uncertain(intent);
      throw error;
    }
  }

  async resolvePartnerCreate(
    key: string,
    operation: OutcomeOperation = "PARTNER_CREATE",
    abandon?: { reason: string; consequenceConfirmed: true },
    targetId?: string,
  ): Promise<unknown> {
    const authority = captureRequestAuthority(),
      actor = observedCatalogueActor();
    const identity = demoIdentities.find((value) => actor?.userId === demoActor(value).userId);
    if (
      !identity ||
      !actor ||
      authorityFingerprint(actor) !== authorityFingerprint(demoActor(identity))
    )
      throw new CyraError({
        code: "AUTH_PERMISSION_DENIED",
        message: "Only unchanged named synthetic authority may reconcile this workspace.",
        requestId: "local-synthetic-workspace",
      });
    await this.ensureSession();
    assertRequestAuthority(authority);
    const response = await this.post(abandon ? "mutation-abandon" : "mutation-outcome", {
      protocol: demoWorkspaceProtocol,
      identity,
      operation,
      key,
      ...(targetId === undefined ? {} : { targetId }),
      ...abandon,
    });
    assertRequestAuthority(authority);
    const result = await this.result(response);
    assertRequestAuthority(authority);
    return result;
  }
}

export function demoMutationOperationFingerprint(
  actorId: string,
  method: DemoWorkspaceMethod,
  args: unknown[],
): Promise<string> {
  if (method === "saveLegalDraft" && args[1] !== undefined)
    return mutationOperationFingerprint(
      actorId,
      { method: "POST", url: new URL("/__cyra_demo_csv/command", window.location.origin).href },
      JSON.stringify([method, args[1]]),
    );
  if (method === "updatePartnerScheduleStatus" && args[1] !== undefined) {
    const binding = demoUpdateBinding(method, args);
    return mutationOperationFingerprint(
      actorId,
      { method: "POST", url: new URL("/__cyra_demo_csv/command", window.location.origin).href },
      JSON.stringify([method, binding?.targetId, binding?.sourceOperation]),
    );
  }
  if (
    method === "mutateAccess" ||
    method === "mutateAdministrator" ||
    method === "updateActionPolicy" ||
    method === "decideApprovalRequest" ||
    method === "controlPartnerImport"
  ) {
    const binding = demoUpdateBinding(method, args);
    return mutationOperationFingerprint(
      actorId,
      { method: "POST", url: new URL("/__cyra_demo_csv/command", window.location.origin).href },
      JSON.stringify([method, binding?.targetId, binding?.sourceOperation]),
    );
  }
  return mutationOperationFingerprint(
    actorId,
    { method: "POST", url: new URL("/__cyra_demo_csv/command", window.location.origin).href },
    JSON.stringify([method, typeof args[0] === "string" ? args[0] : ""]),
  );
}
export const localDemoWorkspace = new DemoWorkspaceConnection();
export function createDemoWorkspaceRepository(
  fallback: AdminRepository | (() => Promise<AdminRepository>),
  connection = localDemoWorkspace,
): AdminRepository {
  const lazy = typeof fallback === "function";
  return new Proxy(lazy ? ({} as AdminRepository) : fallback, {
    get(target, key, receiver) {
      if (typeof key !== "string" || !isDemoWorkspaceMethod(key))
        return Reflect.get(target, key, receiver);
      return async (...args: unknown[]) => {
        const authority = captureRequestAuthority();
        const capability = await connection.capability();
        assertRequestAuthority(authority);
        if (capability.available) return connection.invoke(key, args);
        if (key.includes("PartnerImport") && key !== "getPartnerImportDefinition")
          throw new CyraError({
            code: "DEMO_HELPER_UNAVAILABLE",
            requestId: "local-synthetic-workspace",
            message:
              "Recoverable CSV imports require the local helper. Start it and reload. Static/in-memory demo imports are unavailable.",
          });
        const repository = lazy ? await fallback() : target;
        assertRequestAuthority(authority);
        const signal = args.at(-1);
        if (signal instanceof AbortSignal) signal.throwIfAborted();
        return (repository[key] as (...values: unknown[]) => Promise<unknown>).apply(
          repository,
          args,
        );
      };
    },
  });
}
